import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook to handle text truncation and expansion based on content overflow.
 *
 * This hook:
 * - Detects whether the text overflows its container.
 * - Allows toggling between truncated (collapsed) and expanded states.
 * - Dynamically adjusts when the screen size changes.
 * - Ensures "Show More" and "Show Less" buttons appear only when needed.
 *
 * @returns {Object} Hook return values:
 * - `textRef`: Ref for the text container (assign it to the `div` containing the text).
 * - `isExpanded`: Boolean indicating if the text is currently expanded.
 * - `isTruncated`: Boolean indicating if the text is being truncated (i.e., it overflows).
 * - `toggleExpansion`: Function to toggle between expanded and collapsed states (attach it to the button).
 */
const useTruncatableText = (initialHeight: number = 60) => {
  const [isExpanded, setIsExpanded] = useState(false); // Controls if text is expanded or truncated
  const [isTruncated, setIsTruncated] = useState(false); // Determines if truncation is needed
  const textRef = useRef<HTMLDivElement>(null); // Reference to the text container

  /**
   * Checks whether the text is overflowing (i.e., if truncation is needed).
   * If truncation is no longer needed (text fully fits), it auto-collapses.
   */
  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      const element = textRef.current;

      // Measure the actual visible height of the element
      const visibleHeight = element.getBoundingClientRect().height;

      // Measure the full content height (scrollHeight includes overflow)
      // const fullContentHeight = element.scrollHeight;
      let isOverflowing: boolean;
      if (visibleHeight > initialHeight) {
        isOverflowing = true;
      } else {
        isOverflowing = false;
      }

      setIsTruncated(isOverflowing);

      // if not overflowing set isExpanded to false
      if (!isOverflowing) {
        setIsExpanded(false);
      }
    }
  }, []);

  /**
   * Runs truncation check on component mount and on window resize.
   * Ensures the text truncates/expands dynamically as the screen size changes.
   */
  useEffect(() => {
    checkTruncation(); // Initial check

    // Listen for window resize events
    window.addEventListener('resize', checkTruncation);

    // Cleanup function to remove event listener on unmount
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation]);

  /**
   * Toggles text expansion/collapsing state.
   * If collapsing (`isExpanded = false`), recheck truncation after state update.
   */
  const toggleExpansion = () => {
    setIsExpanded(prev => {
      const newExpandedState = !prev;

      // If collapsing, check truncation again after state update
      if (!newExpandedState) {
        setTimeout(() => checkTruncation(), 0);
      }

      return newExpandedState;
    });
  };

  return {
    textRef, // Assign this to the div containing the text
    isExpanded, // Use this to apply styles when text is expanded, and to conditionally render the "Show More"/"Show Less" button
    isTruncated, // Use this to conditionally render the "Show More"/"Show Less" button
    toggleExpansion, // Attach this function to the "Show More"/"Show Less" button
  };
};

export default useTruncatableText;
